import React from 'react';
import { Typography } from 'antd';

const { Title, Paragraph, Text } = Typography;

/**
 * StockEvolutionDetails component
 * Displays detailed information about how stock evolution is calculated
 */
const StockEvolutionDetails = () => {
  return (
      <Typography>
        <Title level={4}>How Stock Evolution is Calculated</Title>
        
        <Paragraph>
          The stock evolution prediction shows how product stock levels are expected to change over time based on current inventory, 
          upcoming deliveries, and projected consumption from marketing campaigns.
        </Paragraph>
        
        <Title level={5}>Starting Point</Title>
        <Paragraph>
          <Text strong>Current Stock:</Text> We start with the current inventory level for each product variation.
          This is shown in the &quot;Current Stock&quot; column and is the baseline for all predictions.
        </Paragraph>
        
        <Title level={5}>Daily Stock Calculation</Title>
        <Paragraph>
          For each day in the selected date range, we calculate the expected stock level by:
        </Paragraph>
        
        <Paragraph>
          <ol>
            <li>
              <Text strong>Adding Incoming Deliveries:</Text> Any purchase orders scheduled to arrive on that day 
              are added to the stock level. This can be toggled on/off using the &quot;Include/Exclude Delivery&quot; filter.
            </li>
            <li>
              <Text strong>Subtracting Consumption from Marketing Campaigns:</Text> We consider all media plans that 
              might affect the product during this period. For each campaign:
              <ul>
                <li>We look at campaigns that started up to 100 days before the start date and all campaigns within the date range</li>
                <li>The impact of each campaign is distributed over time using a response curve model</li>
                <li>Daily consumption is calculated as: Mail Quantity × Response Rate × Items Per Order × Daily Distribution Percentage</li>
                <li>This consumption is subtracted from the stock level</li>
              </ul>
            </li>
          </ol>
        </Paragraph>
        
        <Title level={5}>Order Planning Information</Title>
        <Paragraph>
          The table includes additional columns to help with order planning:
          <ul>
            <li><Text strong>Stock on Day 60:</Text> Projected stock level 60 days from today</li>
            <li><Text strong>Quantity to Order:</Text> Suggested order quantity based on the selected target date</li>
            <li><Text strong>Order Date:</Text> Recommended date to place an order (60 days before the first negative stock date)</li>
            <li><Text strong>Order Priority:</Text> Days remaining until the recommended order date (color-coded)</li>
            <li><Text strong>Delivery Priority:</Text> Days between out-of-stock date and latest significant delivery date</li>
          </ul>
        </Paragraph>
        
        <Title level={5}>Target Date Selection</Title>
        <Paragraph>
          The &quot;Target Date&quot; filter allows you to select a future date for calculating the quantity to order:
          <ul>
            <li>By default, it&apos;s set to 90 days from today</li>
            <li>You can select any future date (past dates are disabled)</li>
            <li>The quantity to order is calculated based on the projected stock level on the selected target date</li>
            <li>If the projected stock on the target date is negative, the absolute value is suggested as the quantity to order</li>
          </ul>
        </Paragraph>
        
        <Title level={5}>Delivery Priority Calculation</Title>
        <Paragraph>
          The delivery priority is calculated as the difference between:
          <ul>
            <li>The first date when stock is projected to become negative (out-of-stock date)</li>
            <li>The latest expected significant delivery date (only considering deliveries with at least 10% remaining)</li>
          </ul>
          <Text strong>Interpretation:</Text>
          <ul>
            <li>Positive value: You&apos;ll run out of stock AFTER your last delivery arrives (good)</li>
            <li>Negative value: You&apos;ll run out of stock BEFORE your last delivery arrives (urgent)</li>
            <li>Higher absolute values indicate greater urgency or safety margin</li>
          </ul>
        </Paragraph>
        
        <Title level={5}>Color Coding</Title>
        <Paragraph>
          <ul>
            <li><Text style={{ color: 'red' }}>Red numbers</Text>: Negative stock (below 0)</li>
            <li><Text style={{ color: 'orange' }}>Orange numbers</Text>: Low stock (below 100 units)</li>
            <li><Text>Black numbers</Text>: Healthy stock levels</li>
            <li><Text strong>Priority Colors:</Text> Range from red (urgent) through yellow to green (less urgent)</li>
          </ul>
        </Paragraph>
        
        <Paragraph>
          This prediction helps identify potential stock issues before they occur, allowing for better 
          inventory management and campaign planning.
        </Paragraph>
      </Typography>
  );
};

export default StockEvolutionDetails;
